import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  LogOut,
  FileText,
  TrendingUp,
  Building2,
  BedDouble,
  KeyRound,
  LayoutDashboard,
  Briefcase,
  Building,
} from "lucide-react";
import { toast, Toaster } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ih_investor_token";

export default function Investor() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  // Auto-redeem if URL contains ?code=XXX (from copied admin link)
  useEffect(() => {
    if (token) return;
    const params = new URLSearchParams(window.location.search);
    const urlCode = params.get("code");
    if (!urlCode) return;
    axios
      .post(`${API}/investor/redeem-code`, { code: urlCode.trim().toUpperCase() })
      .then((res) => {
        localStorage.setItem(TOKEN_KEY, res.data.token);
        setToken(res.data.token);
        toast.success("Accesso effettuato");
        // Clean ?code from URL
        window.history.replaceState({}, "", window.location.pathname);
      })
      .catch(() => {
        toast.error("Codice del link non valido");
      });
  }, [token]);

  return (
    <div data-testid="investor-page" className="pt-28">
      <Toaster theme="dark" position="top-right" richColors />
      {token ? (
        <Dashboard
          token={token}
          onLogout={() => {
            localStorage.removeItem(TOKEN_KEY);
            setToken(null);
          }}
        />
      ) : (
        <Login
          onSuccess={(t) => {
            localStorage.setItem(TOKEN_KEY, t);
            setToken(t);
          }}
        />
      )}
    </div>
  );
}

function Login({ onSuccess }) {
  const [mode, setMode] = useState("code"); // 'code' | 'credentials'
  const [code, setCode] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submitCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/investor/redeem-code`, {
        code: code.trim().toUpperCase(),
      });
      toast.success("Accesso effettuato");
      onSuccess(res.data.token);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Codice non valido");
    } finally {
      setLoading(false);
    }
  };

  const submitCreds = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/investor/login`, {
        email,
        password,
      });
      toast.success("Accesso effettuato");
      onSuccess(res.data.token);
    } catch (err) {
      toast.error("Credenziali non valide");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="min-h-[80svh] flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="relative w-full max-w-md"
      >
        <div className="absolute -top-40 -right-40 h-[400px] w-[400px] rounded-full bg-[#C48F56] blur-[180px] opacity-20" />
        <div className="absolute -bottom-40 -left-40 h-[300px] w-[300px] rounded-full bg-[#70142A] blur-[140px] opacity-20" />

        <div className="relative glass-strong rounded-2xl p-10">
          <div className="h-12 w-12 rounded-full bg-white text-black grid place-items-center mx-auto">
            <KeyRound size={18} />
          </div>
          <h1 className="mt-6 font-display text-4xl text-center tracking-tight">
            Area Investitori
          </h1>
          <p className="mt-3 text-center text-white/60 text-sm">
            Accedi per consultare report, documenti e aggiornamenti riservati.
          </p>

          {/* Mode tabs */}
          <div className="mt-8 grid grid-cols-2 gap-1 p-1 rounded-full border border-white/10 bg-black/30">
            <button
              type="button"
              onClick={() => setMode("code")}
              data-testid="investor-tab-code"
              className={`text-xs uppercase tracking-[0.2em] font-mono py-2.5 rounded-full transition-colors ${
                mode === "code"
                  ? "bg-white text-black"
                  : "text-white/60 hover:text-white"
              }`}
            >
              Codice
            </button>
            <button
              type="button"
              onClick={() => setMode("credentials")}
              data-testid="investor-tab-credentials"
              className={`text-xs uppercase tracking-[0.2em] font-mono py-2.5 rounded-full transition-colors ${
                mode === "credentials"
                  ? "bg-white text-black"
                  : "text-white/60 hover:text-white"
              }`}
            >
              Email & Password
            </button>
          </div>

          {mode === "code" ? (
            <form onSubmit={submitCode} className="mt-8 space-y-5">
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">
                  Codice di accesso
                </label>
                <input
                  type="text"
                  required
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  data-testid="investor-code"
                  spellCheck="false"
                  autoComplete="off"
                  className="w-full bg-transparent border-b border-white/15 focus:border-[#C48F56] outline-none py-3 text-lg tracking-[0.2em] font-mono uppercase"
                  placeholder="XXX-XXX-XXX"
                />
                <p className="mt-3 text-xs text-white/40 leading-relaxed">
                  Il codice ti è stato fornito direttamente da Invest Hospitality.
                </p>
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="investor-code-submit"
                className="w-full rounded-full bg-white text-black px-6 py-3 text-sm font-medium hover:bg-[#C48F56] disabled:opacity-50 transition-colors"
              >
                {loading ? "Verifica…" : "Entra"}
              </button>
              <p className="text-[10px] text-white/35 leading-relaxed text-center mt-4">
                Accedendo dichiari di aver letto l'
                <a
                  href="/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline decoration-dotted underline-offset-2 hover:text-[#C48F56]"
                >
                  Informativa Privacy
                </a>
                .
              </p>
            </form>
          ) : (
            <form onSubmit={submitCreds} className="mt-8 space-y-5">
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="investor-email"
                  className="w-full bg-transparent border-b border-white/15 focus:border-[#C48F56] outline-none py-3 text-base"
                  placeholder="tu@azienda.it"
                />
              </div>
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="investor-password"
                  className="w-full bg-transparent border-b border-white/15 focus:border-[#C48F56] outline-none py-3 text-base"
                  placeholder="••••••••"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="investor-submit"
                className="w-full rounded-full bg-white text-black px-6 py-3 text-sm font-medium hover:bg-[#C48F56] disabled:opacity-50 transition-colors"
              >
                {loading ? "Accesso…" : "Accedi"}
              </button>
              <p className="text-[10px] text-white/35 leading-relaxed text-center mt-4">
                Accedendo dichiari di aver letto l'
                <a
                  href="/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline decoration-dotted underline-offset-2 hover:text-[#C48F56]"
                >
                  Informativa Privacy
                </a>
                .
              </p>
            </form>
          )}
        </div>
      </motion.div>
    </section>
  );
}

function Dashboard({ token, onLogout }) {
  const [overview, setOverview] = useState(null);
  const [docs, setDocs] = useState([]);
  const [sections, setSections] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [tab, setTab] = useState("dashboard");
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const headers = { Authorization: `Bearer ${token}` };
    setLoading(true);
    setError(false);

    // Fetch each endpoint independently so a single failure never
    // wipes out the entire investor area. Only a "hard" auth failure
    // (401 on the primary overview call) forces a re-login.
    const safe = (p) =>
      p.then((r) => ({ ok: true, data: r.data })).catch((err) => ({
        ok: false,
        status: err?.response?.status || 0,
        data: null,
      }));

    Promise.all([
      safe(axios.get(`${API}/investor/overview`, { headers })),
      safe(axios.get(`${API}/investor/documents`, { headers })),
      safe(axios.get(`${API}/investor/sections`, { headers })),
      safe(axios.get(`${API}/investor/hotels-data`, { headers })),
    ]).then(([o, d, s, h]) => {
      if (cancelled) return;
      // Only treat as fatal if BOTH primary calls fail with 401
      // (i.e. token really invalid). Transient errors keep the page usable.
      const allUnauthorized = [o, d, s, h].every(
        (r) => !r.ok && r.status === 401
      );
      if (allUnauthorized) {
        setError(true);
        setLoading(false);
        return;
      }
      setOverview(o.ok ? o.data : {});
      setDocs(d.ok && Array.isArray(d.data) ? d.data : []);
      setSections(s.ok && Array.isArray(s.data) ? s.data : []);
      setHotels(h.ok && Array.isArray(h.data) ? h.data : []);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [token]);

  const doLogout = async () => {
    try {
      await axios.post(
        `${API}/investor/logout`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (e) {}
    onLogout();
  };

  if (error) {
    return (
      <div className="min-h-[60svh] grid place-items-center px-6 text-center">
        <div>
          <p className="text-white/60">Sessione scaduta.</p>
          <button
            onClick={onLogout}
            className="mt-4 rounded-full border border-white/15 px-5 py-2 text-sm"
          >
            Torna al login
          </button>
        </div>
      </div>
    );
  }

  if (!overview || loading) {
    return (
      <div className="min-h-[60svh] grid place-items-center text-white/40">
        Caricamento…
      </div>
    );
  }

  const portfolio = overview?.portfolio || {};
  const milestones = Array.isArray(overview?.milestones) ? overview.milestones : [];

  const TABS = [
    { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { id: "profilo", label: "Profilo & Bilanci", icon: Briefcase },
    { id: "alberghi", label: "I nostri Alberghi", icon: Building },
  ];

  return (
    <section className="px-6 md:px-12 max-w-[1440px] mx-auto py-10">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#C48F56]">
            Area Investitori
          </div>
          <h1 className="mt-2 font-display text-5xl md:text-6xl tracking-tightest">
            {TABS.find((t) => t.id === tab)?.label}
          </h1>
        </div>
        <button
          onClick={doLogout}
          data-testid="investor-logout"
          className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm hover:bg-white/5"
        >
          <LogOut size={14} /> Esci
        </button>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 p-1 rounded-full border border-white/10 bg-black/30 mb-10 w-fit">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`investor-tab-${t.id}`}
              className={`inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-mono px-5 py-2.5 rounded-full transition-colors ${
                tab === t.id
                  ? "bg-white text-black"
                  : "text-white/60 hover:text-white"
              }`}
            >
              <Icon size={13} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "dashboard" && (
        <DashboardTab
          portfolio={portfolio}
          milestones={milestones}
          docs={docs}
          token={token}
        />
      )}
      {tab === "profilo" && <ProfiloTab sections={sections} />}
      {tab === "alberghi" && <AlberghiTab hotels={hotels} />}
    </section>
  );
}

function DashboardTab({ portfolio, milestones, docs, token }) {
  // Safe defaults so missing/partial API responses never crash the render.
  const p = portfolio || {};
  const totalRooms = Number(p.total_rooms) || 0;
  const occupancy = Number(p.occupancy_ytd) || 0;
  return (
    <>
      {/* KPIs */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <Kpi
          icon={<Building2 size={16} />}
          label="Hotel operativi"
          value={p.operational_hotels ?? "—"}
          sub={`+${p.pipeline_hotels ?? 0} in pipeline`}
        />
        <Kpi
          icon={<BedDouble size={16} />}
          label="Camere totali"
          value={totalRooms.toLocaleString("it-IT")}
          sub={`+${p.pipeline_rooms ?? 0} in arrivo`}
        />
        <Kpi
          icon={<TrendingUp size={16} />}
          label="Occupancy YTD"
          value={`${(occupancy * 100).toFixed(1)}%`}
          sub="Media portafoglio"
        />
        <Kpi
          icon={<TrendingUp size={16} />}
          label="RevPAR"
          value={`€ ${p.revpar_eur ?? "—"}`}
          sub={`ADR € ${p.adr_eur ?? "—"}`}
        />
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        {/* Documents */}
        <div className="lg:col-span-8 rounded-2xl border border-white/10 bg-[#0F0F14] p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display text-2xl tracking-tight">
              Documenti riservati
            </h2>
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
              {docs.length} file
            </span>
          </div>
          <ul className="divide-y divide-white/5">
            {docs.map((d) => (
              <li
                key={d.id}
                className="py-4 flex items-center gap-4"
                data-testid={`doc-${d.id}`}
              >
                <div className="h-10 w-10 rounded-lg border border-white/10 grid place-items-center text-[#C48F56]">
                  <FileText size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{d.title}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1">
                    {d.category} · {d.period} · {d.size} · agg.{" "}
                    {new Date(d.updated).toLocaleDateString("it-IT")}
                  </div>
                </div>
                {d.file_url ? (
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await axios.post(
                          `${API}/investor/documents/${d.id}/track-download`,
                          {},
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                      } catch (e) {
                        // Non-blocking — il download parte comunque
                      }
                      const url = d.file_url.startsWith("http")
                        ? d.file_url
                        : `${process.env.REACT_APP_BACKEND_URL}${d.file_url}`;
                      window.open(url, "_blank", "noopener");
                    }}
                    data-testid={`doc-download-${d.id}`}
                    className="text-xs uppercase tracking-[0.2em] text-white/60 hover:text-[#C48F56]"
                  >
                    Scarica
                  </button>
                ) : (
                  <span
                    title="File non ancora caricato"
                    className="text-xs uppercase tracking-[0.2em] text-white/20 cursor-not-allowed"
                  >
                    Non disponibile
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Milestones */}
        <div className="lg:col-span-4 rounded-2xl border border-white/10 bg-[#0F0F14] p-8">
          <h2 className="font-display text-2xl tracking-tight mb-6">
            Milestone 2026–2027
          </h2>
          <ul className="space-y-4">
            {milestones.map((m, i) => (
              <li key={i} className="relative pl-6">
                <span className="absolute left-0 top-1.5 h-2 w-2 rounded-full bg-[#C48F56]" />
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
                  {m.date}
                </div>
                <div className="mt-1 text-white/80 text-sm">{m.title}</div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </>
  );
}

function renderBody(body) {
  if (!body) return null;
  const blocks = body.split(/\n\s*\n/);
  return blocks.map((block, i) => {
    const lines = block.split("\n").filter((l) => l.trim());
    const allBullets = lines.length > 0 && lines.every((l) => l.trim().startsWith("- "));
    if (allBullets) {
      return (
        <ul key={i} className="space-y-2 my-4">
          {lines.map((l, j) => (
            <li key={j} className="relative pl-5 text-white/75 leading-relaxed">
              <span className="absolute left-0 top-2.5 h-1.5 w-1.5 rounded-full bg-[#C48F56]" />
              {l.replace(/^-\s*/, "")}
            </li>
          ))}
        </ul>
      );
    }
    return (
      <p key={i} className="my-4 text-white/75 leading-relaxed">
        {block}
      </p>
    );
  });
}

function ProfiloTab({ sections }) {
  if (!sections.length) {
    return (
      <div className="rounded-2xl border border-white/10 bg-[#0F0F14] p-16 text-center">
        <Briefcase size={28} className="mx-auto text-white/30" />
        <p className="mt-4 text-white/50 text-sm">
          Nessuna sezione pubblicata. Configurale dal CMS → Sezioni Investitori.
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-10">
      {sections.map((s) => (
        <article
          key={s.id}
          className="rounded-2xl border border-white/10 bg-[#0F0F14] p-8 md:p-12"
          data-testid={`section-${s.slug}`}
        >
          {s.eyebrow && (
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#C48F56]">
              {s.eyebrow}
            </div>
          )}
          <h2 className="mt-2 font-display text-3xl md:text-4xl tracking-tight">
            {s.title}
          </h2>
          <div className="mt-6 prose-invest max-w-none">
            {renderBody(s.body)}
          </div>
          {s.images?.length > 0 && (
            <div
              className={`mt-8 grid gap-6 ${
                s.images.length === 1
                  ? "grid-cols-1"
                  : "grid-cols-1 md:grid-cols-2"
              }`}
            >
              {s.images.map((img, i) => (
                <figure key={i}>
                  <img
                    src={
                      img.url.startsWith("http")
                        ? img.url
                        : `${process.env.REACT_APP_BACKEND_URL}${img.url}`
                    }
                    alt={img.caption || s.title}
                    className="w-full rounded-xl border border-white/10 bg-black/30"
                  />
                  {img.caption && (
                    <figcaption className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 text-center">
                      {img.caption}
                    </figcaption>
                  )}
                </figure>
              ))}
            </div>
          )}
        </article>
      ))}
    </div>
  );
}

function AlberghiTab({ hotels }) {
  const enriched = hotels.filter(
    (h) => h.investor_summary || h.investor_image || h.position_bullets?.length
  );
  if (!enriched.length) {
    return (
      <div className="rounded-2xl border border-white/10 bg-[#0F0F14] p-16 text-center">
        <Building size={28} className="mx-auto text-white/30" />
        <p className="mt-4 text-white/50 text-sm">
          Nessun hotel ha ancora i dati investitori configurati. Compila i
          campi nella sezione "Area Investitori" del CMS → Hotel.
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-12">
      {enriched.map((h) => (
        <article
          key={h.id}
          className="rounded-2xl border border-white/10 bg-[#0F0F14] overflow-hidden"
          data-testid={`investor-hotel-${h.slug}`}
        >
          {(h.investor_photo || h.image) && (
            <div className="aspect-[16/6] bg-black overflow-hidden">
              <img
                src={
                  (h.investor_photo || h.image).startsWith("http")
                    ? h.investor_photo || h.image
                    : `${process.env.REACT_APP_BACKEND_URL}${h.investor_photo || h.image}`
                }
                alt={h.name}
                className="w-full h-full object-cover"
              />
            </div>
          )}
          <div className="p-8 md:p-12">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#C48F56]">
              {"★".repeat(h.stars)} · {h.area}
            </div>
            <h2 className="mt-2 font-display text-3xl md:text-4xl tracking-tight">
              {h.name}
            </h2>
            {h.investor_opening && (
              <div className="mt-2 text-white/50 text-sm font-mono uppercase tracking-[0.15em]">
                {h.investor_opening}
              </div>
            )}
            {h.investor_summary && (
              <p className="mt-6 text-white/75 leading-relaxed max-w-3xl">
                {h.investor_summary}
              </p>
            )}
            <div className="mt-8 grid md:grid-cols-2 gap-8">
              {h.position_bullets?.length > 0 && (
                <BulletList title="Posizione" items={h.position_bullets} />
              )}
              {h.services_bullets?.length > 0 && (
                <BulletList title="Servizi" items={h.services_bullets} />
              )}
            </div>
            {h.map_embed_url && (
              <div className="mt-8 aspect-[16/7] rounded-xl overflow-hidden border border-white/10">
                <iframe
                  src={h.map_embed_url}
                  title={`Mappa ${h.name}`}
                  loading="lazy"
                  className="w-full h-full"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            )}
            {h.investor_image && (
              <div className="mt-8">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-3">
                  Performance economica
                </div>
                <img
                  src={
                    h.investor_image.startsWith("http")
                      ? h.investor_image
                      : `${process.env.REACT_APP_BACKEND_URL}${h.investor_image}`
                  }
                  alt={`Performance ${h.name}`}
                  className="w-full rounded-xl border border-white/10 bg-black/30"
                />
              </div>
            )}
          </div>
        </article>
      ))}
    </div>
  );
}

function BulletList({ title, items }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#C48F56] mb-3">
        {title}
      </div>
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li key={i} className="relative pl-5 text-white/75 leading-relaxed">
            <span className="absolute left-0 top-2.5 h-1.5 w-1.5 rounded-full bg-[#C48F56]" />
            {it}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Kpi({ icon, label, value, sub }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0F0F14] p-6">
      <div className="flex items-center gap-2 text-[#C48F56]">
        {icon}
        <span className="font-mono text-[10px] uppercase tracking-[0.25em]">
          {label}
        </span>
      </div>
      <div className="mt-4 font-display text-4xl tracking-tight">{value}</div>
      <div className="mt-1 text-xs text-white/40">{sub}</div>
    </div>
  );
}
